import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import QrCodeModal from './qrCodeModal';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps,
  FilterValueProps, mappingFilterFields,
  mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import { deleteProjectsService, getAllProjectService } from 'common/services/extends/project';
import { DepartmentItemTypes } from 'common/services/extends/project/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateDDMMYYYY, formatDateTime } from 'common/utils/functions';

export type TableDataTypes = {
  id: number;
  code?: string;
  customerName?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  websiteDomain?: string;
  createdAt?: string;
  updatedAt?: string;
  accountant?: DepartmentItemTypes
  operation?: DepartmentItemTypes,
  canDelete?: boolean;
};

const ProgramManagement: React.FC<ActiveRoles> = ({
  roleIndex,
  roleDelete,
  roleCreate
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);

  const queryClient = useQueryClient();

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [qrCode, setQrCode] = useState('');
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const queryKey = ['project-list', keyword, currentPage, currentView, selectedFilterList];

  /* Queries */
  const { data: listProjectData, isLoading } = useQuery(
    queryKey,
    () => getAllProjectService({
      ...(keyword && { keyword }),
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter
    }),
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['project-delete'],
    async (ids: number[]) => deleteProjectsService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('program.code'),
      dataIndex: 'code',
      key: 'code',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.code}
        </Typography.Text>
      ),
    },
    {
      title: t('program.nameCustomer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 220,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.customerName}
        </Typography.Text>
      ),
    },
    {
      title: t('program.name'),
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.name}
        </Typography.Text>
      ),
    },
    {
      title: t('program.fromDate'),
      dataIndex: 'fromDate',
      key: 'fromDate',
      width: 100,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.fromDate, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('program.toDate'),
      dataIndex: 'toDate',
      key: 'toDate',
      width: 100,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.toDate, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('program.personInChargeAC'),
      dataIndex: 'accountant',
      key: 'accountant',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.accountant?.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('program.personInChargeOP'),
      dataIndex: 'operation',
      key: 'operation',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.operation?.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (_name: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateTime(data.createdAt)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.updatedAt'),
      dataIndex: 'updatedAt',
      width: 170,
      key: 'updatedAt',
      render: (_name: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateTime(data.updatedAt)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              icon={<EyeOutlined />}
              disabled={!roleIndex}
              onClick={() => navigate(`${ROUTE_PATHS.PROGRAM_DETAIL}?projectId=${data.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('system.delete')}>
            <Button
              icon={<DeleteOutlined />}
              disabled={!roleDelete || !data.canDelete}
              onClick={(e) => {
                if (e.stopPropagation) e.stopPropagation();
                Modal.confirm({
                  className: 't-pagetable_deleteLanguageModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => deleteMutate([data.id]),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSetCurrentPage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', page.toString());
    setCurrentPage(page);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
    handleSetCurrentPage(1);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
    handleSetCurrentPage(1);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('project', advancedFilter),
    [advancedFilter]
  );

  const onDelete = async (data: TableDataTypes[], code?: string) => {
    switch (code) {
      case 'allRow':
      case 'all': {
        deleteMutate(data.map((item) => item.id));
        break;
      }
    }
  };

  useEffect(() => {
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      ...pageParam && { page: pageParam }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterList]);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableData: TableDataTypes[] = useMemo(() => {
    if (!listProjectData?.data) return [];

    return listProjectData.data.map((item) => ({
      id: item.projectData?.id || -1,
      code: item.projectData?.code,
      customerName: item.projectData?.customerName,
      name: item.projectData?.name,
      fromDate: item.projectData?.fromDate,
      toDate: item.projectData?.toDate,
      websiteDomain: item.projectData?.websiteDomain,
      createdAt: item.projectData?.createdAt,
      updatedAt: item.projectData?.updatedAt,
      accountant: item.accountant,
      operation: item.operation,
      canDelete: item.projectData?.canDelete,
    }));
  }, [listProjectData]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.program')}
        rightHeader={(
          <Button
            type="primary"
            onClick={() => navigate(ROUTE_PATHS.PROGRAM_DETAIL)}
            disabled={!roleCreate}
          >
            <PlusOutlined />
            {t('system.create')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={onDelete}
          handleSearch={setKeyword}
          isLoading={isLoading || deleteLoading}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
          tableProps={{
            initShowColumns: ['code', 'customerName', 'name', 'fromDate', 'toDate', 'accountant', 'operation', 'createdAt', 'updatedAt', 'action'],
            columns,
            pageData: tableData || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: listProjectData?.meta.total || 1,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields
          }}
        />
      </div>
      <QrCodeModal qrCode={qrCode} handleClose={() => setQrCode('')} />
    </>
  );
};

export default ProgramManagement;
